/**
 * Plutops Site Configuration — single source of truth for all displayed company info.
 *
 * HOW TO USE:
 *   1. Find every line marked `// REPLACE` below.
 *   2. Replace the placeholder value with your real info.
 *   3. Commit + push — the site auto-redeploys.
 *
 * The defaults below are the ORIGINAL AI-generated placeholder content from
 * Emergent.sh. They are not real. Anything you don't replace will continue
 * to show as fake info on the public site.
 *
 * HIDING SECTIONS:
 *   - Set `team: []` to hide the leadership team section on the About page.
 *   - Set any `social.*` value to `null` to hide that link.
 *   - Set any `stats.*` value to `null` to hide that stat card.
 *   - `locations` must always have at least one entry.
 */

export const siteConfig = {
  company: {
    name: 'Plutops',
    legalName: 'Plutops Technologies',
    tagline: 'Precision Digital Engineering', // REPLACE: your real tagline
    description:
      // REPLACE: your real one-paragraph company description
      'Leading IT services company offering software development, web development, app development, and IT consulting services worldwide.',
    logo: 'P', // single letter shown in header/footer logo badge
    foundedYear: 2024, // REPLACE: actual founding year
  },

  contact: {
    email: 'contact@plutops.com', // REPLACE: your real email
    supportEmail: 'admin@plutops.com', // REPLACE: your support email (can match `email`)
    phone: '+91 7772968401', // REPLACE: your real phone (include country code)
    responseTimeHours: 24,
  },

  // REPLACE: add your real office(s). Set latitude/longitude to 0 if unknown.
  // Keep at least one entry. Each location shows on Home, About, and Footer.
  locations: [
    {
      city: 'New York',
      country: 'USA',
      address: '450 Buel Ave, Staten Island, NY 10305, USA',
      latitude: 40.7128,
      longitude: -74.0060,
      description:
        'IT services and software development company serving businesses across North America.',
    },
    {
      city: 'London',
      country: 'UK',
      address: 'EC2A 4BX Bonhill St, London',
      latitude: 51.5074,
      longitude: -0.1278,
      description:
        'Expert IT consulting and custom software development for UK enterprises.',
    },
    {
      city: 'Bangalore',
      country: 'India',
      address: 'Prestige Atlanta, Koramangala, Bengaluru',
      latitude: 12.9716,
      longitude: 77.5946,
      description:
        'Offshore software development center with skilled engineers.',
    },
  ],

  social: {
    linkedin: 'https://linkedin.com/company/plutops', // REPLACE or set to null to hide
    twitter: 'https://twitter.com/plutops', // REPLACE or null
    github: 'https://github.com/plutops', // REPLACE or null
  },

  // Numbers shown on the About page stats band. Use a string with a suffix
  // (e.g. '100+'). Set to null to hide an individual stat card.
  // ALL FOUR VALUES BELOW ARE AI-GENERATED — REPLACE with real numbers, or
  // set to null to hide if you don't have credible data yet.
  stats: {
    projectsDelivered: '10+', // REPLACE or null
    enterpriseClients: '5+', // REPLACE or null
    clientRetention: '96%', // REPLACE or null
    teamMembers: '10+', // REPLACE or null
  },

  // Leadership team shown on About page. THE 4 PEOPLE BELOW ARE AI-GENERATED
  // FAKE NAMES. The `image` URLs use ui-avatars.com to render initials in the
  // brand color as a clear placeholder — swap to real headshot URLs when you
  // replace the names, or set `team: []` to hide the section entirely.
  team: [
    {
      name: 'Sagar Samvedi',
      role: 'CEO',
      image: 'https://ui-avatars.com/api/?name=Sagar+Samvedi&background=cc5500&color=fff&size=300&bold=true',
      bio: '6+ years in tech leadership',
      linkedin: null,
    },
    {
      name: 'Ishaan Juneja',
      role: 'Head of Marketing',
      image: 'https://ui-avatars.com/api/?name=Ishaan+Juneja&background=cc5500&color=fff&size=300&bold=true',
      bio: '6+ years in marketing and client handling',
      linkedin: null,
    },
    {
      name: 'Vibhanshu Mishra',
      role: 'Director',
      image: 'https://ui-avatars.com/api/?name=Vibhanshu+Mishra&background=cc5500&color=fff&size=300&bold=true',
      bio: '6+ years in US operations',
      linkedin: null,
    },
  ],

  // REPLACE: real mission statement
  mission:
    'To empower businesses with innovative software development solutions that drive growth, efficiency, and competitive advantage. We believe in building lasting partnerships and delivering exceptional value through precision engineering and IT consulting expertise.',

  // REPLACE: real vision statement
  vision:
    'To be the premier IT services partner for enterprises worldwide, recognized for our innovation in web development, app development, and commitment to excellence. We envision a future where technology seamlessly enables business success.',

  // REPLACE: real reasons clients should pick you (5 short bullets)
  whyChooseUs: [
    'Proven track record with 10+ clients',
    'Agile software development methodology',
    'Dedicated support and maintenance',
    'Transparent communication throughout',
    'Cost-effective IT solutions without compromise',
  ],

  // Areas/countries served — used only in SEO Organization schema, not visible UI
  areasServed: [
    'United States',
    'United Kingdom',
    'India',
    'Canada',
    'Australia',
  ],

  // Services keywords used only in SEO schema. Does NOT change the actual
  // /services pages — those are loaded from the database via the admin.
  expertise: [
    'Software Development',
    'Web Development',
    'Mobile App Development',
    'IT Consulting',
    'Cloud Services',
    'AI Solutions',
  ],
};

/**
 * Backwards-compatible `BRAND` shape used by existing imports across the
 * codebase. New code should import `siteConfig` directly instead.
 */
export const BRAND = {
  name: siteConfig.company.name,
  tagline: siteConfig.company.tagline,
  description: siteConfig.company.description,
  logo: siteConfig.company.logo,
  email: siteConfig.contact.email,
  phone: siteConfig.contact.phone,
  locations: siteConfig.locations,
  social: siteConfig.social,
};

export default siteConfig;
